<template>
  <div class="catalog-manager">
    <div class="page-header">
      <h1>{{ $t('bo.catalog.title') }}</h1>
      <p>{{ $t('bo.catalog.subtitle') }}</p>
    </div>

    <div v-if="loading" class="loading">{{ $t('bo.common.loading') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>

    <template v-else>
      <p class="odoo-note">{{ $t('bo.catalog.odooNote') }}</p>

      <section v-for="section in sections" :key="section.key" class="catalog-section">
        <h2 class="section-title">{{ section.title }}</h2>
        <p v-if="!section.items.length" class="empty">{{ $t('bo.catalog.empty') }}</p>
        <table v-else class="catalog-table">
          <thead>
            <tr>
              <th>{{ $t('bo.catalog.name') }}</th>
              <th>{{ $t('bo.catalog.code') }}</th>
              <th>{{ $t('bo.catalog.monthly') }}</th>
              <th>{{ $t('bo.catalog.annualPerMonth') }}</th>
              <th>{{ $t('bo.catalog.annualTotal') }}</th>
              <th>{{ $t('bo.catalog.status') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in section.items" :key="`${section.key}-${item.id}`" :class="{ inactive: !item.is_active }">
              <td class="name">{{ item.name }}</td>
              <td><code>{{ item.code }}</code></td>
              <td>
                <input v-model="drafts[draftKey(section.key, item)].price_monthly" type="number" min="0" step="0.01" class="price-input" />
              </td>
              <td>
                <input v-model="drafts[draftKey(section.key, item)].price_annual" type="number" min="0" step="0.01" class="price-input" />
              </td>
              <td class="total">{{ annualTotal(drafts[draftKey(section.key, item)].price_annual) }}</td>
              <td>
                <span :class="item.is_active ? 'badge-active' : 'badge-inactive'">
                  {{ item.is_active ? $t('bo.catalog.active') : $t('bo.catalog.inactive') }}
                </span>
              </td>
              <td class="actions">
                <button
                  class="btn-save"
                  :disabled="saving === draftKey(section.key, item) || !isDirty(section.key, item)"
                  @click="save(section.key, item)"
                >
                  {{ saving === draftKey(section.key, item) ? $t('bo.common.saving') : $t('bo.catalog.save') }}
                </button>
                <span v-if="saved === draftKey(section.key, item)" class="saved">{{ $t('bo.catalog.saved') }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import catalogService from '@/services/catalogService'

const { t } = useI18n()

const catalog = ref({ plans: [], addons: [] })
const drafts  = reactive({})
const loading = ref(false)
const error   = ref('')
const saving  = ref('')
const saved   = ref('')

const sections = computed(() => [
  { key: 'plans', title: t('bo.catalog.plans'), items: catalog.value.plans ?? [] },
  { key: 'addons', title: t('bo.catalog.addons'), items: catalog.value.addons ?? [] },
])

const draftKey = (sectionKey, item) => `${sectionKey}-${item.id}`
const toInput = (value) => (value === null || value === undefined ? '' : String(value))
const toPayload = (value) => (value === '' || value === null || value === undefined ? null : Number(value))

// Prix annuel saisi PAR MOIS (comme sur l'ecran d'abonnement) : total facture sur l'annee.
const annualTotal = (perMonth) => {
  const value = toPayload(perMonth)
  return value === null ? '-' : (value * 12).toFixed(2)
}

const resetDraft = (sectionKey, item) => {
  drafts[draftKey(sectionKey, item)] = {
    price_monthly: toInput(item.price_monthly),
    price_annual: toInput(item.price_annual),
  }
}

const isDirty = (sectionKey, item) => {
  const draft = drafts[draftKey(sectionKey, item)]
  return toPayload(draft.price_monthly) !== item.price_monthly || toPayload(draft.price_annual) !== item.price_annual
}

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    catalog.value = await catalogService.list()
    for (const section of sections.value) {
      section.items.forEach(item => resetDraft(section.key, item))
    }
  } catch {
    error.value = t('bo.catalog.failedLoad')
  } finally {
    loading.value = false
  }
}

const save = async (sectionKey, item) => {
  const key = draftKey(sectionKey, item)
  const draft = drafts[key]
  const payload = { price_monthly: toPayload(draft.price_monthly), price_annual: toPayload(draft.price_annual) }
  saving.value = key
  saved.value = ''
  try {
    const updated = sectionKey === 'plans'
      ? await catalogService.updatePlan(item.id, payload)
      : await catalogService.updateAddon(item.id, payload)
    Object.assign(item, updated)
    resetDraft(sectionKey, item)
    saved.value = key
  } catch (err) {
    window.alert(err?.response?.data?.message || t('bo.catalog.failedSave'))
  } finally {
    saving.value = ''
  }
}

onMounted(load)
</script>

<style scoped>
.catalog-manager { padding: 2rem; }
.page-header { margin-bottom: 1.5rem; }
.page-header h1 { font-size: 1.75rem; font-weight: 700; color: #1f2937; margin: 0 0 0.25rem; }
.page-header p { color: #6b7280; font-size: 0.875rem; margin: 0; }
.loading { padding: 3rem; text-align: center; color: #6b7280; }
.error-msg { padding: 1rem; color: #dc2626; background: #fef2f2; border-radius: 0.5rem; }
.odoo-note { font-size: 0.85rem; color: #1e40af; background: #eff6ff; border-radius: 0.5rem; padding: 0.75rem 1rem; margin: 0 0 1.5rem; }
.catalog-section { margin-bottom: 2rem; }
.section-title { font-size: 1.125rem; font-weight: 700; color: #1f2937; margin: 0 0 0.75rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb; }
.empty { color: #6b7280; font-size: 0.875rem; }
.catalog-table { width: 100%; border-collapse: collapse; background: white; border: 1px solid #e5e7eb; border-radius: 0.75rem; overflow: hidden; }
.catalog-table th { text-align: left; font-size: 0.75rem; text-transform: uppercase; color: #6b7280; background: #f9fafb; padding: 0.6rem 0.75rem; }
.catalog-table td { padding: 0.6rem 0.75rem; border-top: 1px solid #f3f4f6; font-size: 0.875rem; color: #1f2937; }
.catalog-table tr.inactive td { color: #9ca3af; }
.name { font-weight: 600; }
.price-input { width: 7rem; padding: 0.35rem 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem; }
.total { font-variant-numeric: tabular-nums; }
.badge-active { font-size: 0.75rem; color: #065f46; background: #d1fae5; border-radius: 999px; padding: 0.1rem 0.5rem; }
.badge-inactive { font-size: 0.75rem; color: #6b7280; background: #f3f4f6; border-radius: 999px; padding: 0.1rem 0.5rem; }
.actions { white-space: nowrap; }
.btn-save { padding: 0.35rem 0.9rem; border: 1px solid #3b82f6; color: #3b82f6; background: white; border-radius: 0.375rem; cursor: pointer; }
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
.saved { margin-left: 0.5rem; color: #059669; font-size: 0.8rem; }
</style>
